import React from 'react';

import Box from '@mui/material/Box';
import TableContainer from '@mui/material/TableContainer';

import { lightPalette, palette, shape } from '@app/theme';
import { useHorizontalOverflow } from '@utils/hooks';

import type { ScrollableTableProperties } from './ScrollableTable.types';

/**
Ширина подсказки о скрытых колонках
*/
const SCROLL_HINT_WIDTH = 40;

/**
 * Подсказка растворяет содержимое у края: текст, уходящий под границу, выцветает в фон,
 * и обрезка перестаёт выглядеть концом таблицы. Первые 40% держат цвет фона плотно —
 * иначе на плотной вёрстке дизайн-системы (Swiss, border-first) переход слишком слаб,
 * чтобы его заметить. Тень вместо заливки не годится: тени здесь на порядок легче
 * (alpha 0.02–0.06), и заметная была бы громче всего остального в интерфейсе.
 */
const buildScrollHintSx = (edge: 'left' | 'right') => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  [edge]: 0,
  width: `${SCROLL_HINT_WIDTH}px`,
  // Подсказка чисто декоративная: не должна перехватывать ни клики, ни скролл
  pointerEvents: 'none',
  background: `linear-gradient(to ${edge === 'left' ? 'right' : 'left'}, ${
    lightPalette.background.paper
  } 40%, transparent)`,
});

/**
 * Обёртка для таблиц отчёта: даёт горизонтальный скролл и подсказку о скрытых колонках.
 *
 * Ключевой момент — разделение ролей. Скругление углов требует `overflow: hidden`, но этот
 * шорthand убивает обе оси сразу, вместе с горизонтальным скроллом. Поэтому обрезает внешний
 * элемент, а скроллит вложенный: на самом скроллере `overflow: hidden` стоять не должен.
 */
export const ScrollableTable: React.FC<ScrollableTableProperties> = ({
  children,
  bordered = true,
  ariaLabel,
  sx,
  dataTest = 'scrollable-table',
}) => {
  const { scrollerReference, hasHiddenContentStart, hasHiddenContentEnd, handleScroll } =
    useHorizontalOverflow<HTMLDivElement>();

  // Клавиатурная остановка и landmark нужны только у области, которая действительно скроллится.
  // Безусловные создавали бы на широком экране пустые таб-стопы у каждой таблицы отчёта, а
  // одинаково названные регионы (одна и та же таблица в нескольких секциях) — дублирующиеся landmark.
  const isScrollable = hasHiddenContentStart || hasHiddenContentEnd;

  return (
    <Box
      data-testid={dataTest}
      sx={[
        {
          position: 'relative',
          overflow: 'hidden',
          ...(bordered && {
            border: `1px solid ${lightPalette.border.default}`,
            borderRadius: `${shape.borderRadius}px`,
          }),
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <TableContainer
        ref={scrollerReference}
        onScroll={handleScroll}
        data-testid={`${dataTest}_scroller`}
        // Скроллящаяся область должна доставаться с клавиатуры, иначе колонки за правым краем
        // остаются недостижимы ровно так же, как без скролла вовсе
        {...(isScrollable ? { tabIndex: 0 } : {})}
        {...(isScrollable && ariaLabel ? { role: 'region', 'aria-label': ariaLabel } : {})}
        sx={{
          overflowX: 'auto',
          overflowY: 'hidden',
          // Кольцо фокуса рисуем внутрь: дефолтное браузерное уходит наружу и срезается
          // клиппером внешней обёртки — таб-стоп без видимого фокуса нарушает WCAG 2.4.7
          '&:focus-visible': {
            outline: `2px solid ${palette.primary.main}`,
            outlineOffset: '-2px',
          },
        }}
      >
        {children}
      </TableContainer>

      {hasHiddenContentStart && (
        <Box aria-hidden='true' data-testid={`${dataTest}_hint-start`} sx={buildScrollHintSx('left')} />
      )}
      {hasHiddenContentEnd && (
        <Box aria-hidden='true' data-testid={`${dataTest}_hint-end`} sx={buildScrollHintSx('right')} />
      )}
    </Box>
  );
};
